<template>
  <BaseWidget 
    title="Previous check-ins"
    :show-info-icon="true"
    info-tooltip="Check-in history within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/2056065-how-do-check-ins-work-in-optix', text: 'Learn about Check-ins' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <MetricValue 
      v-else
      :value="formattedCheckIns" 
      :subtitle="dateRangeLabel" 
    >
      <template #comparison>
        <ComparisonChip 
          :change="changePercentage" 
          :previous-value="formattedPreviousCheckIns"
          :previous-period-dates="previousTimestamps"
          :date-range-preset="dateRange"
          label="Previous check-ins"
        />
      </template>
    </MetricValue>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useCheckInsData, useGraphQLData } from '../composables/useWidgetData.js'
import { getDemoCheckInsData } from '../services/demoData.js'
import { DATE_RANGE_LABELS, getDateRangeTimestamps, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_7_days')

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Today'
})

// Get location IDs from parent window URL
const locationIds = useLocationFilter()

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Get current period timestamps based on selected date range
const getCurrentTimestamps = () => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
}

// Get previous period timestamps for comparison
const getPreviousTimestamps = () => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
}

// Get previous period timestamps for tooltip
const previousTimestamps = computed(() => {
  return getPreviousTimestamps()
})

// Fetch current period data
const { data, loading, error, cleanup } = useCheckInsData({
  dateRangePreset: dateRange,
  variables: computed(() => {
    const timestamps = getCurrentTimestamps()
    return {
      timestamp_from: timestamps.from_timestamp,
      timestamp_to: timestamps.to_timestamp,
      location_id: locationIds.value.length > 0 ? locationIds.value : undefined
    }
  }),
  priority: props.priority ?? 1 // Use provided priority or default to 1
})

// Fetch previous period data for comparison
const checkInsQuery = `
  query numberOfCheckins($timestamp_from: Int!, $timestamp_to: Int!, $user_id: [ID!], $location_id: [ID!]) {
    checkins(timestamp_from: $timestamp_from, timestamp_to: $timestamp_to, user_id: $user_id, location_id: $location_id) {
      total
    }
  }
`

const { 
  data: previousData, 
  loading: previousLoading, 
  error: previousError, 
  cleanup: previousCleanup 
} = useGraphQLData({
  dataKey: 'checkins_previous',
  query: checkInsQuery,
  variables: computed(() => {
    const timestamps = getPreviousTimestamps()
    return {
      timestamp_from: timestamps.from_timestamp,
      timestamp_to: timestamps.to_timestamp,
      user_id: [],
      location_id: locationIds.value.length > 0 ? locationIds.value : undefined
    }
  }),
  operationName: 'numberOfCheckins',
  priority: props.priority ?? 1,
  getDemoData: () => getDemoCheckInsData('previous')
})

const formattedCheckIns = computed(() => {
  if (!data.value?.checkins?.total) return '0'
  const total = data.value.checkins.total
  // Format large numbers with commas
  return total.toLocaleString()
})

const formattedPreviousCheckIns = computed(() => {
  if (!previousData.value?.checkins?.total) return null
  const total = previousData.value.checkins.total
  return total.toLocaleString()
})

const changePercentage = computed(() => {
  const current = data.value?.checkins?.total ?? 0
  const previous = previousData.value?.checkins?.total ?? 0
  
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previous === 0 || previous === null || previous === undefined) return null
  
  const change = ((current - previous) / previous) * 100
  const roundedChange = Math.round(change * 10) / 10 // Round to 1 decimal place
  
  return roundedChange
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
:deep(.base-widget) {
  overflow: visible;
}

:deep(.widget-header) {
  overflow: visible;
}

:deep(.widget-title-row) {
  overflow: visible;
  position: relative;
  z-index: 1;
}

:deep(.comparison-text) {
  position: relative;
  z-index: 10;
}

:deep(.widget-content) {
  min-height: 0;
  overflow: hidden;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}
</style>
